import React, { HTMLAttributes } from 'react';
import { cn } from '../../utils/cn';

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
    variant?: 'default' | 'success' | 'warning' | 'error' | 'info' | 'primary' | 'secondary' | 'danger';
    size?: 'xs' | 'sm' | 'md' | 'lg';
    dot?: boolean;
    outline?: boolean;
}

const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(
    (
        {
            className,
            variant = 'default',
            size = 'sm',
            dot = false,
            outline = false,
            children,
            ...props
        },
        ref
    ) => {
        const baseStyles = `
      inline-flex items-center gap-1.5
      font-medium rounded-full
      transition-all duration-200
    `;

        const variantStyles = outline
            ? {
                default: 'bg-transparent border border-gray-300 text-gray-700 dark:border-gray-600 dark:text-gray-300',
                primary: 'bg-transparent border border-primary-500 text-primary-600 dark:text-primary-400',
                success: 'bg-transparent border border-success-500 text-success-600 dark:text-success-400',
                warning: 'bg-transparent border border-warning-500 text-warning-600 dark:text-warning-400',
                error: 'bg-transparent border border-error-500 text-error-600 dark:text-error-400',
                info: 'bg-transparent border border-info-500 text-info-600 dark:text-info-400',
                secondary: 'bg-transparent border border-gray-400 text-gray-600 dark:border-gray-500 dark:text-gray-400',
                danger: 'bg-transparent border border-rose-500 text-rose-600 dark:text-rose-400',
            }
            : {
                default: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
                primary: 'bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-300',
                success: 'bg-success-100 text-success-700 dark:bg-success-900 dark:text-success-300',
                warning: 'bg-warning-100 text-warning-700 dark:bg-warning-900 dark:text-warning-300',
                error: 'bg-error-100 text-error-700 dark:bg-error-900 dark:text-error-300',
                info: 'bg-info-100 text-info-700 dark:bg-info-900 dark:text-info-300',
                secondary: 'bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
                danger: 'bg-rose-100 text-rose-700 dark:bg-rose-900 dark:text-rose-300',
            };

        const sizeStyles = {
            xs: 'px-2 py-0.5 text-xs',
            sm: 'px-2.5 py-1 text-xs',
            md: 'px-3 py-1 text-sm',
            lg: 'px-3.5 py-1.5 text-sm',
        };

        const dotColors = {
            default: 'bg-gray-500',
            primary: 'bg-primary-500',
            success: 'bg-success-500',
            warning: 'bg-warning-500',
            error: 'bg-error-500',
            info: 'bg-info-500',
            secondary: 'bg-gray-400',
            danger: 'bg-rose-500',
        };

        return (
            <span
                ref={ref}
                className={cn(
                    baseStyles,
                    variantStyles[variant],
                    sizeStyles[size],
                    className
                )}
                {...props}
            >
                {dot && (
                    <span
                        className={cn(
                            'w-1.5 h-1.5 rounded-full',
                            dotColors[variant]
                        )}
                    />
                )}
                {children}
            </span>
        );
    }
);

Badge.displayName = 'Badge';

export default Badge;
